import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../../../../assets/images/brand/logo-black.png';
import LogoWhite from '../../../../../assets/images/brand/logo-white-color.svg';
import LogoIcon from '../../../../../assets/images/brand/only-logo.png';
import { useUserStore } from '../../../../../modules/Auth/stores/authStore';
import {
  getUserPermissions,
  hasExactPermission,
} from '../../../../accessControl/utils';
import { Button } from '../../../../components/atoms/Button';
import DropDown from '../../../../components/molecules/Dropdown';
import { Typography } from '../../../../components/molecules/Typography';
import NotificationCard from '../../../../components/organism/NotificationCard';
import { notificationsHook } from '../../../../hooks/notificationsHook';
// import { useNotificationsStore } from '../../../../stores/useNotifications';
import SearchInput from '../searchInput';
import {
  IMenuItem,
  ISideMenuItem,
  menuItems,
  sideMenuItems,
} from './menuItems';
import './styles.css';
import { row } from './variants';
import { useAccessControlStore } from '../../../../stores/accessControlStore';

const MenuBar = () => {
  const { pathname } = useLocation();

  const { mutate } = notificationsHook({ page: 1 });
  // const { hasNotificationPendindg, countNotificationPendindg } =
  //   useNotificationsStore();

  const [isMenuVisible, setMenuVisible] = useState(true);
  const { user: meData } = useUserStore();
  const { isFreeUser } = useAccessControlStore();

  const setActive = (url: string) => {
    if (pathname?.includes(url)) {
      return 'selected';
    } else {
      return 'focusable';
    }
  };

  const setActiveItem = (url: string) => {
    if (pathname === '/') {
      if (url === '/') {
        return 'selected';
      } else {
        return 'hoverable';
      }
    }

    if (url !== '/') {
      if (pathname?.includes(url)) {
        return 'selected';
      } else {
        return 'hoverable';
      }
    } else {
      return 'hoverable';
    }
  };

  const handleAssineAgora = () => {
    const urlCheckout = "https://checkout.aprovauniversitario.com.br/lead?oferta=a2mN40000001z1RIAQ&productCode=aprovauniversitario-aprova-universitario&seller=performance&source=plataforma_universitario&utm_source=plataforma_universitario&utm_medium=organic&utm_campaign=lancamento_plataforma&utm_content=conteudo_bloqueado"
    window.open(urlCheckout, '_blank');
  }

  useEffect(() => {
    mutate({ page: 1, limit: 15 });
  }, [mutate]);

  useEffect(() => {
    if (window.screen.width < 901) {
      const handleScroll = () => {
        if (window.scrollY > 100) {
          setMenuVisible(false);
        } else {
          setMenuVisible(true);
        }
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const bgColorDropDown = meData?.isDarkMode
    ? { backgroundColor: '#000C29' }
    : { backgroundColor: '#ffffff' };
  //@todo: ver como utilizar os design tokens

  const filteredMenuItems = useMemo(() => {
    const userPermission = getUserPermissions(meData);
    const menuItemsFiltered: IMenuItem[] = [];

    menuItems.forEach((menuItem) => {
      if (
        menuItem.permission &&
        hasExactPermission(userPermission, menuItem.permission) &&
        menuItem.items.length <= 0
      ) {
        menuItemsFiltered.push(menuItem);
        return;
      }

      const filteredItems = menuItem.items?.filter(
        (item) =>
          hasExactPermission(userPermission, item.permission) ||
          !item.permission
      );

      if (filteredItems && filteredItems.length > 0) {
        menuItemsFiltered.push({ ...menuItem, items: filteredItems });
      }
    });

    return menuItemsFiltered;
  }, [meData]);

  const filteredSideMenuItems = useMemo(() => {
    const userPermission = getUserPermissions(meData);
    const sideMenuItemsFiltered: ISideMenuItem[] = [];

    sideMenuItems.forEach((sideMenuItem) => {
      if (!sideMenuItem.permission && sideMenuItem.items.length <= 0) {
        sideMenuItemsFiltered.push(sideMenuItem);
        return;
      }

      if (
        sideMenuItem.permission &&
        sideMenuItem.items.length <= 0 &&
        hasExactPermission(userPermission, sideMenuItem.permission)
      ) {
        sideMenuItemsFiltered.push(sideMenuItem);
      }

      const filteredItems = sideMenuItem.items?.filter(
        (item) =>
          !item.permission ||
          hasExactPermission(userPermission, item.permission)
      );

      if (filteredItems && filteredItems.length > 0) {
        sideMenuItemsFiltered.push({ ...sideMenuItem, items: filteredItems });
      }
    });

    return sideMenuItemsFiltered;
  }, [meData]);

  return (
    <div className={row({ visibility: isMenuVisible ? 'show' : 'hide' })}>
      <div className='flex h-full w-fit flex-1 flex-row items-center justify-start gap-5 p-0'>
        <Link to='/' className='xs:hidden xl:block'>
          <img
            src={meData?.isDarkMode ? LogoWhite : Logo}
            alt='Logo'
            className='h-[24px]'
          />
        </Link>
        <Link to='/' className='xs:hidden lg:block xl:hidden'>
          <img src={LogoIcon} alt='Logo' width='16.6px' height='24px' />
        </Link>
        {isFreeUser && (
          <Button LeftIcon="editor_choice" iconType='material' size='small' variant='contained' className='hidden lg:flex' onClick={handleAssineAgora}>
            Assine Agora
          </Button>
        )}
        <div className='xs:block lg:hidden'>
          <Button
            iconVariant='rounded'
            size='small'
            variant='hoverable'
            LeftIcon={'search'}
            iconType='material'
            onlyIcon
            data-tooltip-id={'menu-tooltip-search'}
          />
          <DropDown
            id={`menu-tooltip-search`}
            offset={20}
            position='bottom-end'
            style={bgColorDropDown}
          >
            <SearchInput />
          </DropDown>
        </div>
      </div>
      <div className='flex-2 w-fit'>
        <div className='m-auto h-full w-fit flex-row items-center xs:hidden lg:flex lg:gap-xs xl:gap-lg'>
          {filteredMenuItems.map((it, i) => {
            return (
              <Fragment key={i}>
                <Button
                  size='regular'
                  variant={setActiveItem(it.href || '')}
                  LeftIcon={it.icon}
                  iconType={it.iconType}
                  data-tooltip-id={`top-menu-${i}`}
                  asLink={it.clickable}
                  to={it.clickable ? it.href : ''}
                  iconVariant='rounded'
                  className='relative'
                >
                  {it.label === 'Minha Área' ? (
                    <Typography className='hidden xl:block'>
                      {it.label}
                    </Typography>
                  ) : (
                    it.label
                  )}
                </Button>
                {it.items && it.items.length > 0 && (
                  <DropDown
                    id={`top-menu-${i}`}
                    offset={20}
                    position='bottom-end'
                    style={bgColorDropDown}
                  >
                    <div
                      id='dropContentMenu'
                      className='max-h-[70vh] overflow-y-auto'
                    >
                      {it.items.map((menuItems, i) => (
                        <Button
                          iconVariant='rounded'
                          variant={setActive(menuItems.link)}
                          key={i}
                          LeftIcon={menuItems.icon}
                          iconType={menuItems.iconType}
                          iconSize={menuItems.iconSize}
                          className={`relative w-full text-left`}
                          asLink
                          to={menuItems.link}
                        >
                          <div className='flex items-center gap-md'>
                            {menuItems.label.replace('Humana', '')}
                          </div>
                        </Button>
                      ))}
                    </div>
                  </DropDown>
                )}
              </Fragment>
            );
          })}
        </div>
        <div className='m-auto h-full w-fit flex-row items-center xs:flex lg:hidden'>
          <Link to='/'>
            <img
              src={meData?.isDarkMode ? LogoWhite : Logo}
              alt='Logo black'
              width='153.57px'
              height='18px'
            />
          </Link>
        </div>
      </div>
      <div className='w-fit flex-1'>
        <div className='flex h-full w-full flex-row items-center justify-end gap-y-lg p-0'>
          <Button
            iconVariant='rounded'
            size='regular'
            variant='hoverable'
            LeftIcon='search'
            data-tooltip-id={`side-menu-search`}
            className={'hidden lg:flex'}
          />
          <DropDown
            id={`side-menu-search`}
            offset={10}
            position='bottom-end'
            style={bgColorDropDown}
          >
            <SearchInput />
          </DropDown>
          {/* <Button
            iconVariant='rounded'
            size='regular'
            variant='hoverable'
            LeftIcon='notifications_none'
            data-tooltip-id={`side-menu-news`}
            className='relative'
          >
            {hasNotificationPendindg && (
              <span
                className='absolute right-4 top-2 inline-flex -translate-y-1/2 translate-x-1/2 transform items-center
              justify-center rounded-circular bg-feedback-color-error-pure px-1.5 py-1 text-xs
               font-bold leading-none text-mono-color-white'
              >
                {countNotificationPendindg}
              </span>
            )}
          </Button> */}
          <DropDown
            id={`side-menu-news`}
            offset={20}
            position='bottom-end'
            className='max-h-[calc(100vh-250px)] max-w-[95vw] overflow-auto bg-mono-color-darkest p-3 lg:max-h-[470px]'
          >
            <NotificationCard />
          </DropDown>

          <span className={'hidden lg:flex'}>
            {filteredSideMenuItems.map((it, i) => {
              if (it.icon === 'menu') {
                return (
                  <Fragment key={i}>
                    <Button
                      iconVariant='rounded'
                      size='regular'
                      variant='hoverable'
                      LeftIcon={it.icon}
                      data-tooltip-id={`side-menu-${i}`}
                    />
                    {it.items.length > 0 && (
                      <DropDown
                        id={`side-menu-${i}`}
                        offset={20}
                        position='bottom-end'
                        style={bgColorDropDown}
                      >
                        <div className='bg-color-lightest'>
                          {it.items.map((item: any, i) => {
                            return (
                              <Button
                                iconVariant='rounded'
                                variant={setActive(item.link)}
                                key={i}
                                LeftIcon={item.icon}
                                iconType={item.iconType}
                                iconSize={item.iconSize}
                                className='w-full text-left'
                                target={item.externalLink ? '_blank' : ''}
                                asLink
                                to={item.link}
                                disabled={item.disabled}
                              >
                                {item.label}
                              </Button>
                            );
                          })}
                        </div>
                      </DropDown>
                    )}
                  </Fragment>
                );
              }
            })}
          </span>
        </div>
      </div>
      {isFreeUser && (
      <Button size='small' variant='contained' className='lg:hidden flex h-fit' onClick={handleAssineAgora}>
          Assine Agora
        </Button>
      )}
    </div>
  );
};

export default MenuBar;
